import { useMemo } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { Box, Typography } from '@mui/material'
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../interaxDesignSystemComponents'
import { PageHeader } from '../../../shared/components/PageHeader'
import VideoCard from '../../../components/Videos/VideoCard'
import { ExpertContentArticleCard } from '../../../components/Videos/ExpertContentArticleCard'
import { useExpertVideos } from '../../../apiLayer'
import { ExpertContentVideo, ExpertContentArticle, Language } from '../../../../../pure-js/types/ExpertVideo'
import { INTERAX_COLORS } from '../../../constants/colors'
import { PageWrapper } from '../../../shared/components'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { filterVideosByActive, filterVideosByLanguage } from '../../../shared/utils/videoFilters'

interface ExpertVideosProps {
  onBackToFaq?: () => void
  /** When false, only videos are shown (e.g. on FAQ/QA pages). Default true. */
  showArticles?: boolean
}

/**
 * Expert Content page component for the facilitator dashboard
 * Displays videos and articles from backend with client-side language filtering
 */
export const ExpertVideosPage = ({ onBackToFaq, showArticles = true }: ExpertVideosProps) => {
  const { t, i18n } = useTranslation()

  // Get current language (en or sv), default to 'en'
  const currentLanguage = useMemo<Language>(() => {
    const lang = i18n.language || 'en'
    return lang === 'sv' ? 'sv' : 'en'
  }, [i18n.language])

  // Fetch all expert content (videos + articles)
  const { data: contentResponse, isLoading, error } = useExpertVideos()

  // Filter content: active only → language; split into videos and articles
  const { videos, articles } = useMemo(() => {
    const allContent = contentResponse?.data || []
    const activeOnly = filterVideosByActive(allContent)
    const filtered = filterVideosByLanguage(activeOnly, currentLanguage)
    const videosList = filtered.filter((item): item is ExpertContentVideo => item.type === 'video')
    const articlesList = filtered.filter((item): item is ExpertContentArticle => item.type === 'article')
    return { videos: videosList, articles: articlesList }
  }, [contentResponse?.data, currentLanguage])

  return (
    <PageWrapper sx={{ backgroundColor: INTERAX_COLORS.offBlack }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Back Button - Top Left */}
        {onBackToFaq && (
          <Box>
            <Button
              variant="secondary"
              onClick={onBackToFaq}
              icon={<ArrowLeftIcon className="w-4 h-4" />}
              iconPosition="left"
            >
              {t('videos.backToFaq')}
            </Button>
          </Box>
        )}

        {/* Header Section */}
        <PageHeader
          badgeText={t('videos.expertContentBadge') || 'EXPERT CONTENT'}
          badgeIcon={<DocumentTextIcon />}
          title={t('videos.title')}
          subtitle={t('videos.subtitle')}
        />

        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}
          >
            <Spinner size="large" color="sun" text={t('videos.loading') || 'Loading videos...'} />
          </Box>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              gap: 2
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.redError, textAlign: 'center' }}>
              {t('videos.error') || 'Error loading videos. Please try again.'}
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && !error && videos.length === 0 && (showArticles ? articles.length === 0 : true) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.thunderGrey, textAlign: 'center' }}>
              {t('videos.empty') || 'No expert content available yet.'}
            </Typography>
          </Box>
        )}

        {/* Videos Section */}
        {!isLoading && !error && videos.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography sx={{ fontWeight: 500, fontSize: '20px', color: INTERAX_COLORS.white }}>
              {t('landing.knowledgeHub.videos') || 'Videos'}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
              {videos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </Box>
          </Box>
        )}

        {/* Articles Section - hidden on FAQ/QA pages (showArticles=false) */}
        {showArticles && !isLoading && !error && articles.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: videos.length > 0 ? 4 : 0 }}>
            <Typography sx={{ fontWeight: 500, fontSize: '20px', color: INTERAX_COLORS.white }}>
              {t('landing.knowledgeHub.articles') || 'Articles'}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
              {articles.map((article) => (
                <ExpertContentArticleCard key={article.id} article={article} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </PageWrapper>
  )
}
